import { BaseDomainModel } from '../models/base/base-domain-model';
import { inject, Injectable } from '@angular/core';
import { SubmissionsAPI } from '../api/submissions-api';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { ODataQueryOptions } from '../models/shared/odata-query-options';
import { ODataResponse } from '../models/protocols/odata-response';
import { ChangeNameRequest } from '../models/users/requests/submissionRequests/change-name-request';
import { CreateAttachmentRequest } from '../models/users/requests/create-attachment-request';
import { Asset } from '../models/asset/dto/asset';
import { SubmissionAttachment } from '../models/submissions/submission-attachment';
import { catchError, map, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import { MemberSubmission, SubmissionStakeholder } from '../models/submissions/member-submission';
import { TypeService } from '../services/type/type-service';
import { SubmissionStatus } from '../models/shared/submission-status';
import { SubmissionCreated } from '../models/submissions/submission-created';
import { AttachmentFile } from '../models/users/dto/attachment-file';
import { AttachmentApi } from '../api/attachment-api';
import { SubmissionCommentRequest } from '../models/users/requests/submission-comment-request';
import { SubmissionComment } from '../models/submissions/submission-comment';
import { InternalUserCommentReadReceiptRequest } from '../models/users/requests/internal-user-comment-read-receipt-request';
import { SubmissionSubStatusEnum } from '../models/enum/shared/submission-sub-status.enum';
import { AccountTypeIdEnum } from '../models/enum/shared/account-type-id.enum';
import { UpdateSubmissionOwnerRequest } from '../models/users/requests/update-submission-owner-request';
import { UpdateSubmissionApproverRequest } from '../models/users/requests/update-submission-approver-request';
import { SubmissionRoleTemplate } from '../models/submissions/submission-role-template';
import { MemberSubmissionType } from '../models/submissions/member-submission-type';
import { SubmissionPermissionType } from '../models/permissions/dto/submission-permission-type';
import { SubmissionPermissionWithType } from '../models/permissions/dto/submission-permission-with-type';
import { ChangeAddressRequest } from '../models/users/requests/submissionRequests/change-address-request';
import { MemberSubmissionReminderRequest } from '../models/users/requests/member-submission-reminder-request';
import { HttpResponse } from '@angular/common/http';
import { PortalService } from '../services/portal/portal.service';
import { UpdateContactInfoRequest } from '../models/users/requests/submissionRequests/update-contact-info-request';
import { SubmissionStatusPill } from '../models/shared/submission-status-pill';
import { ChangePersonalInfoRequest } from '../models/users/requests/submissionRequests/change-personal-info-request';
import { ChangeInvestmentInstructionSubmissionRequest } from '../models/users/requests/change-investment-instruction-submission-request';
import { RecentMemberSubmissionResponse } from '../models/submissions/recent-member-submission-response';
import { RelationshipStatusSubmissionRequest } from '../models/submissions/requests/relationship-status-submission-request';
import { RelationshipSubmissionApprovalRequest } from '../models/submissions/requests/relationship-submission-approval-request';
import { UpdateBeneficiarySpousalWaiverRequest } from '../models/submissions/requests/update-beneficiary-spousal-waiver-request';
import { MpBeneficiaryChangeSubmissionRequest } from '../models/submissions/requests/mp-beneficiary-change-submission-request';
import { IpEmployerSubmission } from '../models/submissions/employer-submissions/ip-employer-submission';
import { EpEmployerSubmission } from '../models/submissions/employer-submissions/ep-employer-submission';
import { EpUpdateEmployerAddressRequest } from '../models/users/requests/submissionRequests/ep-update-employer-address-request';
import { OdataUtils } from '../utils/odata-utils';

// Provided by Logged In Scope
@Injectable()
export class SubmissionsDomainModel extends BaseDomainModel {
  constructor(private attachmentApi: AttachmentApi) {
    super();
    this.init();
  }

  private portalService = inject(PortalService);
  private submissionsApi = inject(SubmissionsAPI);
  private typeService = inject(TypeService);

  private _allMemberSubmissions = new BehaviorSubject<MemberSubmission[] | null>(null);
  public allMemberSubmissions$ = this._allMemberSubmissions as Observable<MemberSubmission[]>;

  private _submissionStatuses = new BehaviorSubject<SubmissionStatus[]>([]);
  public submissionStatuses$ = this._submissionStatuses as Observable<SubmissionStatus[]>;

  private _submissionRoleTemplates = new BehaviorSubject<SubmissionRoleTemplate[]>([]);
  public submissionRoleTemplates$ = this._submissionRoleTemplates as Observable<SubmissionRoleTemplate[]>;

  private _memberSubmissionTypes = new BehaviorSubject<MemberSubmissionType[]>([]);
  public memberSubmissionTypes$ = this._memberSubmissionTypes as Observable<MemberSubmissionType[]>;

  public employerSubmissionTypes$ = this.submissionsApi.ipGetEmployerSubmissionTypes().pipe(shareReplay());

  private _submissionPermissionTypes = new BehaviorSubject<SubmissionPermissionType[]>([]);
  public submissionPermissionTypes$ = this._submissionPermissionTypes as Observable<SubmissionPermissionType[]>;

  public submissionStatusPills$: Observable<SubmissionStatusPill[]> = this.submissionStatuses$.pipe(
    map(ss => {
      return ss.flatMap(s => {
        if (s?.subStatuses?.length > 0) {
          return s?.subStatuses.map(st => {
            return new SubmissionStatusPill(st.id, st.name, st.parentStatusId, st.parentStatus);
          });
        }
        return []; // Return an empty array if there are no sub statuses
      });
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  private _allMemberSubmissionsTotalCount = new BehaviorSubject<number | undefined>(undefined);
  public allMemberSubmissionsTotalCount$ = this._allMemberSubmissionsTotalCount as Observable<number | undefined>;

  public init(): void {
    this.fetchSubmissionStatuses();
    this.portalService.isInternal$.once(isInternal => {
      if (isInternal) {
        this.getSubmissionRoleTemplates();
        this.getInternalMemberSubmissionTypes();
        this.getSubmissionPermissionTypes();
      }
    });
  }

  public ipGetMemberSubmissions(oDataQueryOptions: ODataQueryOptions): Observable<ODataResponse<MemberSubmission>> {
    oDataQueryOptions.setExpand(
      'Owner($expand=ProfileBadgeConfiguration)',
      'Approver($expand=ProfileBadgeConfiguration)',
      'Reminder'
    );
    return this.submissionsApi.ipGetMemberSubmissions(oDataQueryOptions).pipe(
      tap(odataRes => this._allMemberSubmissionsTotalCount.next(odataRes['@odata.count'])),
      tap(odataRes => this._allMemberSubmissions.next(odataRes.value)),
      catchError(err => {
        this._allMemberSubmissions.next([]);
        this._allMemberSubmissionsTotalCount.next(0);
        return throwError(() => err);
      })
    );
  }

  public getMemberSubmissions(oDataQueryOptions: ODataQueryOptions): Observable<ODataResponse<MemberSubmission>> {
    return this.submissionsApi.mpGetMemberSubmissions(oDataQueryOptions).pipe(
      catchError(err => {
        this._allMemberSubmissions.next([]);
        this._allMemberSubmissionsTotalCount.next(0);
        return throwError(() => err);
      })
    );
  }

  public getInternalMemberRecentSubmissions(memberId: number): Observable<RecentMemberSubmissionResponse> {
    return this.submissionsApi.getInternalMemberRecentSubmissions(memberId);
  }

  public ipGetMemberSubmissionById(id: string): Observable<MemberSubmission> {
    return this.submissionsApi.ipGetMemberSubmissionById(id);
  }

  public getSubmissionViewersForSubmissionTypeId(subTypeId: string): Observable<SubmissionStakeholder[]> {
    return this.submissionsApi
      .ipGetMemberSubmissionViewersForMemberSubmissionType(subTypeId)
      .pipe(map(res => res.value));
  }

  public createMemberNameChangeSubmission(req: ChangeNameRequest): Observable<SubmissionCreated> {
    return this.portalService.isInternal$.pipe(
      take(1),
      switchMap(isInternal => {
        if (isInternal) {
          return this.submissionsApi.createInternalNameChangeSubmission(req);
        } else {
          return this.submissionsApi.createNameChangeSubmission(req);
        }
      })
    );
  }

  public createMemberPersonalInfoChangeSubmission(req: ChangePersonalInfoRequest): Observable<SubmissionCreated> {
    return this.portalService.isInternal$.pipe(
      take(1),
      switchMap(isInternal => {
        if (isInternal) {
          return this.submissionsApi.createInternalPersonalInfoChangeSubmission(req);
        } else {
          return this.submissionsApi.createPersonalInfoChangeSubmission(req);
        }
      })
    );
  }

  public createMemberAddressChangeSubmission(req: ChangeAddressRequest): Observable<SubmissionCreated> {
    return this.portalService.isInternal$.pipe(
      take(1),
      switchMap(isInternal => {
        if (isInternal) {
          return this.submissionsApi.createInternalAddressChangeSubmission(req);
        } else {
          return this.submissionsApi.createAddressChangeSubmission(req);
        }
      })
    );
  }

  public createUpdateContactInfoSubmission(req: UpdateContactInfoRequest): Observable<SubmissionCreated> {
    return this.portalService.isInternal$.pipe(
      take(1),
      switchMap(isInternal => {
        if (isInternal) {
          return this.submissionsApi.createInternalUpdateContactInformationSubmission(req);
        } else {
          return this.submissionsApi.createUpdateContactInformationSubmission(req);
        }
      })
    );
  }

  public createInternalSubmissionAttachment(
    req: CreateAttachmentRequest,
    submissionId: string
  ): Observable<SubmissionAttachment> {
    return this.submissionsApi.ipCreateMemberSubmissionAttachment(req, submissionId);
  }

  public createInternalMemberInvestmentInstructionsSubmission(
    req: ChangeInvestmentInstructionSubmissionRequest
  ): Observable<SubmissionCreated> {
    return this.submissionsApi.createInternalMemberInvestmentInstructionsSubmission(req);
  }

  public createMemberInvestmentInstructionsSubmission(
    req: ChangeInvestmentInstructionSubmissionRequest
  ): Observable<SubmissionCreated> {
    return this.submissionsApi.createMemberInvestmentInstructionsSubmission(req);
  }

  public addSubmissionComment(req: SubmissionCommentRequest): Observable<SubmissionComment[]> {
    return this.submissionsApi.ipAddMemberSubmissionComment(req);
  }

  public ipSetMemberSubmissionCommentsAsRead(
    req: InternalUserCommentReadReceiptRequest
  ): Observable<HttpResponse<any>> {
    return this.submissionsApi.ipSetMemberSubmissionCommentsAsRead(req);
  }

  public ipSetEmployerSubmissionCommentsAsRead(
    req: InternalUserCommentReadReceiptRequest
  ): Observable<HttpResponse<any>> {
    return this.submissionsApi.ipSetEmployerSubmissionCommentsAsRead(req);
  }

  public getInternalSubmissionAttachments(submissionId: string): Observable<SubmissionAttachment[]> {
    return this.attachmentApi.ipGetMemberSubmissionAttachments(submissionId);
  }

  public uploadAttachmentToS3(file: AttachmentFile): Observable<Asset> {
    return this.attachmentApi.uploadAttachmentFile(file);
  }

  public deleteInternalSubmissionAttachment(attachmentId: string, submissionId: string): Observable<any> {
    return this.attachmentApi.ipDeleteMemberSubmissionAttachment(attachmentId, submissionId);
  }

  public updateSubmissionStatus(status: SubmissionSubStatusEnum, submissionId: string): Observable<MemberSubmission> {
    return this.submissionsApi.ipUpdateMemberSubmissionStatus(status, submissionId);
  }

  public updateRelationshipStatusSubmissionToApproved(
    req: RelationshipSubmissionApprovalRequest
  ): Observable<MemberSubmission> {
    return this.submissionsApi.updateRelationshipSubmissionStatusToApproved(req);
  }

  public ipUpdateBeneficiarySpousalWaiver(req: UpdateBeneficiarySpousalWaiverRequest): Observable<MemberSubmission> {
    return this.submissionsApi.ipUpdateBeneficiarySpousalWaiver(req);
  }

  private fetchSubmissionStatuses(): void {
    this.typeService.submissionStatuses$.once(ss => this._submissionStatuses.next(ss));
  }

  public getFilteredMemberSubmissionOwners(filterString: string): Observable<SubmissionStakeholder[]> {
    const queryOptions = new ODataQueryOptions();
    queryOptions.setFilter(
      `contains(firstName, '${OdataUtils.escapeStringLiteral(filterString)}')` +
        `or contains(lastName, '${OdataUtils.escapeStringLiteral(filterString)}')`
    );
    queryOptions.setExpand(`ProfileBadgeConfiguration`);
    return this.submissionsApi.ipGetMemberSubmissionOwners(queryOptions).pipe(map(res => res.value));
  }

  public getFilteredEmployerSubmissionOwners(filterString: string): Observable<SubmissionStakeholder[]> {
    const queryOptions = new ODataQueryOptions();
    queryOptions.setFilter(
      `contains(firstName, '${OdataUtils.escapeStringLiteral(filterString)}')` +
        `or contains(lastName, '${OdataUtils.escapeStringLiteral(filterString)}')`
    );
    queryOptions.setExpand(`ProfileBadgeConfiguration`);
    return this.submissionsApi.ipGetMemberSubmissionOwners(queryOptions).pipe(map(res => res.value));
  }

  public ipGetMemberSubmissionOwnersForSubmissionTypeId(subTypeId: string): Observable<SubmissionStakeholder[]> {
    return this.submissionsApi.ipGetMemberSubmissionOwnersForSubmissionType(subTypeId).pipe(map(res => res.value));
  }

  public getMemberSubmissionApproversForSubmissionTypeId(subTypeId: string): Observable<SubmissionStakeholder[]> {
    return this.submissionsApi.ipGetMemberSubmissionApproversForSubmissionType(subTypeId).pipe(map(res => res.value));
  }

  public getFilteredMemberSubmissionApprovers(filterString: string): Observable<SubmissionStakeholder[]> {
    const queryOptions = new ODataQueryOptions();
    queryOptions.setFilter(
      `contains(firstName, '${OdataUtils.escapeStringLiteral(filterString)}')` +
        `or contains(lastName, '${OdataUtils.escapeStringLiteral(filterString)}')`
    );
    queryOptions.setExpand(`ProfileBadgeConfiguration`);
    return this.submissionsApi.ipGetMemberSubmissionApprovers(queryOptions).pipe(map(res => res.value));
  }

  public getFilteredEmployerSubmissionApprovers(filterString: string): Observable<SubmissionStakeholder[]> {
    const queryOptions = new ODataQueryOptions();
    queryOptions.setFilter(
      `contains(firstName, '${OdataUtils.escapeStringLiteral(filterString)}')` +
        `or contains(lastName, '${OdataUtils.escapeStringLiteral(filterString)}')`
    );
    queryOptions.setExpand(`ProfileBadgeConfiguration`);
    return this.submissionsApi.ipGetEmployerSubmissionApprovers(queryOptions).pipe(map(res => res.value));
  }

  public getSubmissionRoleTemplates(): void {
    this.submissionsApi
      .getSubmissionRoleTemplates()
      .pipe(map(res => res.value))
      .once(templates => this._submissionRoleTemplates.next(templates));
  }

  public getSubmissionPermissionTypes(): void {
    this.submissionsApi.getSubmissionPermissionTypes().once(res => this._submissionPermissionTypes.next(res));
  }

  public getAllSubmissionPermissions(): Observable<SubmissionPermissionWithType[]> {
    return this.submissionsApi.getAllSubmissionPermissions().pipe(
      map(res => res.value),
      shareReplay({
        bufferSize: 1,
        refCount: true
      })
    );
  }

  public getInternalMemberSubmissionTypes(): void {
    this.submissionsApi
      .getInternalMemberSubmissionTypes()
      .pipe(map(res => res.value))
      .once(types => this._memberSubmissionTypes.next(types));
  }

  public ipUpdateMemberSubmissionOwner(req: UpdateSubmissionOwnerRequest): Observable<MemberSubmission> {
    return this.submissionsApi.ipUpdateMemberSubmissionOwner(req);
  }

  public updateMemberSubmissionApprover(req: UpdateSubmissionApproverRequest): Observable<MemberSubmission> {
    return this.submissionsApi.ipUpdateMemberSubmissionApprover(req);
  }

  public setSubmissionToReadyForApproval(req: UpdateSubmissionApproverRequest): Observable<MemberSubmission> {
    return this.submissionsApi.ipSetMemberSubmissionToReadyForApproval(req);
  }

  public createMemberSubmissionReminder(req: MemberSubmissionReminderRequest): Observable<MemberSubmission> {
    return this.submissionsApi.ipCreateMemberSubmissionReminder(req);
  }

  public deleteMemberSubmissionReminder(submissionId: string, reminderId: string): Observable<HttpResponse<any>> {
    return this.submissionsApi.ipDeleteMemberSubmissionReminder(submissionId, reminderId);
  }

  public mpCreateRelationshipStatusSubmission(req: RelationshipStatusSubmissionRequest): Observable<SubmissionCreated> {
    return this.submissionsApi.mpCreateRelationshipStatusSubmission(req);
  }

  public mpCreateBeneficiaryUpdateSubmission(
    req: MpBeneficiaryChangeSubmissionRequest,
    accountTypeId: AccountTypeIdEnum
  ): Observable<MemberSubmission> {
    return this.submissionsApi.mpCreateBeneficiaryUpdateSubmission(req, accountTypeId);
  }

  public ipGetEmployerSubmissions(
    opts: ODataQueryOptions = new ODataQueryOptions()
  ): Observable<ODataResponse<IpEmployerSubmission>> {
    return this.submissionsApi.ipGetEmployerSubmissions(opts);
  }

  public epGetEmployerSubmissions(
    opts: ODataQueryOptions = new ODataQueryOptions()
  ): Observable<ODataResponse<EpEmployerSubmission>> {
    return this.submissionsApi.epGetEmployerSubmissions(opts);
  }

  public epUpdateEmployerAddress(req: EpUpdateEmployerAddressRequest): Observable<EpEmployerSubmission> {
    return this.submissionsApi.epUpdateEmployerAddress(req);
  }

  public ipDeleteVariableBenefitSubmission(submissionId: number): Observable<HttpResponse<any>> {
    return this.submissionsApi.ipDeleteVariableBenefitSubmission(submissionId);
  }
}
